import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.tsx";
import AppMessageScreen from "@/components/AppMessageScreen";
import { missingClientEnv } from "@/lib/env";
import { initAnalytics } from "@/lib/analytics";
import "./index.css";

// Initialize PostHog before React renders
initAnalytics();

if (missingClientEnv.length > 0) {
  console.error(
    "Missing client environment variables. " +
      `Add ${missingClientEnv.join(", ")} to your .env file or deployment environment.`,
  );
}

class RootErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      const errorText = this.state.error.message.toLowerCase();
      const isConfigError =
        missingClientEnv.length > 0 ||
        errorText.includes("publishable") ||
        errorText.includes("environment");

      return (
        <AppMessageScreen
          title={isConfigError ? "Configuration Error" : "Something went wrong"}
          message={
            isConfigError
              ? "Required client environment variables are missing. Configure them before loading the app."
              : "The application encountered an unexpected error."
          }
          details={
            missingClientEnv.length > 0
              ? `Missing: ${missingClientEnv.join(", ")}`
              : undefined
          }
          actionLabel="Reload page"
          onAction={() => window.location.reload()}
        />
      );
    }

    return this.props.children;
  }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {missingClientEnv.length > 0 ? (
      <AppMessageScreen
        title="Configuration Error"
        message="Required client environment variables are missing. Configure them before loading the app."
        details={`Missing: ${missingClientEnv.join(", ")}`}
      />
    ) : (
      <RootErrorBoundary>
        <App />
      </RootErrorBoundary>
    )}
  </React.StrictMode>
);
